export interface TranslateMatrixValues {
  x: number;
  y: number;
  z: number;
}

export const getTranslateMatrixValues = (
  element: Element
): TranslateMatrixValues => {
  const style = window.getComputedStyle(element);
  const matrix =
    style.transform ||
    (style as CSSStyleDeclaration & { mozTransform?: string }).mozTransform;

  // No transform property. Simply return 0 values.
  if (matrix === "none" || typeof matrix === "undefined") {
    return {
      x: 0,
      y: 0,
      z: 0,
    };
  }

  // Can either be 2d or 3d transform
  const matrixType = matrix.includes("3d") ? "3d" : "2d";
  const matrixMatch = matrix.match(/matrix.*\((.+)\)/);

  if (!matrixMatch) {
    return { x: 0, y: 0, z: 0 };
  }

  const matrixValues = matrixMatch[1].split(", ");

  // 2d matrices have 6 values
  // Last 2 values are X and Y.
  // 2d matrices does not have Z value.
  if (matrixType === "2d") {
    return {
      x: parseInt(matrixValues[4], 10),
      y: parseInt(matrixValues[5], 10),
      z: 0,
    };
  }

  // 3d matrices have 16 values
  // The 13th, 14th, and 15th values are X, Y, and Z
  if (matrixType === "3d") {
    return {
      x: parseInt(matrixValues[12], 10),
      y: parseInt(matrixValues[13], 10),
      z: parseInt(matrixValues[14], 10),
    };
  }

  return { x: 0, y: 0, z: 0 };
};

/**
 * This is an abstraction for the Promise needed as return value by the checkCanFocusTrap option of the focus-trap library,
 * in order to focus animated elements.
 * (Briefly: the focus needs to be delayed for animated elements, otherwise js errors for not found elements or animation flickering can occur)
 *
 * More context here: https://github.com/focus-trap/focus-trap#createoptions (see checkCanFocusTrap)
 * Here: https://focus-trap.github.io/focus-trap/#demo-animated-dialog
 * And here: https://github.com/focus-trap/focus-trap/blob/master/docs/js/animated-dialog.js
 *
 * Creates a Promise with setInterval (set to 4ms) over the result of the passed (func) function and will cycle until
 * the function returns true.
 * The passed function must check for the transitioned property at its final and stable value (to be true).
 * Please check the correctness of the condition inside the passed function,
 * if it never gets true it will cycle indefinitely.
 * A simple console.log placed into the passed function should reveal any condition issue.
 *
 * @param trapContainers - Container element/s on which focus-trap is set.
 * @param func - A function that checks for some (css property value) condition on the container.
 */
export const checkCanFocusTrap = (
  trapContainers: Element[],
  func: (trapContainer: Element) => boolean
): Promise<void[]> => {
  const results = trapContainers.map((trapContainer) => {
    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (func(trapContainer)) {
          resolve();
          clearInterval(interval);
        }
      }, 4);
    });
  });
  return Promise.all(results);
};

// Compensate Assistive Devices Visibility
/*
  When using the focus trap on multiple absolute positioned
  and potentially overlapped sibling elements, there is the need to explicitely tell the
  assistive device which element is in the foreground. All the other siblings must be hidden,
  otherwise the focus could be trapped outside the foreground element.
  Notice that aria-hidden="false" is not recomended.
  Just remove the aria-hidden attribute from the foreground element,
  as suggested here: https://hub.accessible360.com/kb/articles/424
*/
export function compensateADVisibility(
  node: Element,
  visibility: boolean
): void {
  if (!node.parentElement) {
    return;
  }

  if (visibility) {
    Array.from(node.parentElement.children).forEach((element) => {
      element.setAttribute("aria-hidden", "true");
    });
    node.removeAttribute("aria-hidden");
  } else {
    Array.from(node.parentElement.children).forEach((element) => {
      element.setAttribute("aria-hidden", "true");
    });
    const defaultElement = document.querySelector("[data-focus-trap-default]");
    if (defaultElement) {
      defaultElement.removeAttribute("aria-hidden");
    }
  }
}
